const { useCallback, useState, useEffect } = wp.element;
import { useLoadScript, GoogleMap, MarkerClusterer, Autocomplete } from '@react-google-maps/api';

// Hooks
import useDebounce from '../utils/useDebounce';

// Components
import Pin from './pin';
import Locate from './locate';
import Retailers from './retailers';
// Helpers
import { getClosestRetailerPostcode, getMapLanguage } from '../utils/helpers';

const libraries = [ 'places', 'geometry' ];
const htmlElement = document.querySelector( 'html' );

const CURRENT_LOCATION_KEY = 'QalaB2BWholesale_currentLocation';

/**
 * The Map component displays a Google Map with markers representing retailers. It includes
 * features such as autocompleting location searches, user location detection, and clustering
 * for handling multiple markers.
 *
 * @param {Object}   props                              Properties passed to the component.
 * @param {Object[]} props.retailers                    An array of retailer data objects. Each retailer object
 *                                                      should include at least latitude (`lat`) and longitude (`lng`) properties.
 * @param {Object}   props.mapContainerStyle            An object containing inline styles for the map container.
 * @param {Object}   props.options                      Options to customize the map (e.g., marker clustering settings).
 * @param {Object}   props.defaultCenter                An object with `lat` and `lng` properties specifying the map's initial center.
 * @param {boolean}  props.showLogos                    Parameter to show logo.
 * @param {string}   props.displayMode                  The display mode of the retailer (compact or large).
 * @param {number}   props.perPage                      The number of retailers to display per page.
 * @param {boolean}  props.hideAddressField             If true, hide the address field
 * @param {boolean}  props.hidePhoneField               If true, hide the phone field
 * @param {boolean}  props.hideWebsiteField             If true, hide the website field
 * @param {boolean}  props.hideRetailersWithoutLocation If true, hide the retailers without location
 * @return {JSX.Element}  A JSX structure representing the map and related UI elements.
 */
const Map = ( { retailers, showLogos, displayMode, perPage, hideAddressField, hidePhoneField, hideWebsiteField, hideRetailersWithoutLocation, mapContainerStyle, options, defaultCenter } ) => {
	const { retailerTranslations } = window.QalaB2bWholesale.translations;

	const lang = htmlElement.getAttribute( 'lang' );
	const { region, language } = getMapLanguage( lang );

	const { isLoaded, loadError } = useLoadScript( {
		googleMapsApiKey: window.QalaB2bWholesale.googleMapsApiKey,
		libraries,
		region,
		language,
		version: '3.42.9', // Fixes google bug when clicking markers.
	} );

	const [ center, setCenter ] = useState( defaultCenter );
	const [ autocomplete, setAutocomplete ] = useState();
	const [ selectedMarker, setSelectedMarker ] = useState( null );
	const [ mapAPI, setMapAPI ] = useState();
	const [ mapInstance, setMapInstance ] = useState();
	const [ mapBounds, setMapBounds ] = useState( false );
	const [ locationIsSet, setLocationIsSet ] = useState( false );
	const [ postcodeRetailers, setPostcodeRetailers ] = useState( [] );
	const [ inputValue, setInputValue ] = useState( '' );
	const [ showMap, setShowMap ] = useState( true );
	const [ currentLocation, setCurrentLocation ] = useState( null );

	// For Handling the enter key on the search input (get the first autocomplete reasult)
	const handleKeyDown = event => {
		if ( event.key !== 'Enter' ) {
			return;
		}
		event.preventDefault();

		if ( ! autocomplete || ! inputValue ) {
			return;
		}

		const service = new window.google.maps.places.AutocompleteService();
		service.getPlacePredictions(
			{ input: inputValue, bounds: mapBounds },
			( predictions, status ) => {
				if ( status !== window.google.maps.places.PlacesServiceStatus.OK || ! predictions || predictions.length === 0 ) {
					return;
				}

				const firstPrediction = predictions[ 0 ];
				const placesService = new window.google.maps.places.PlacesService( mapInstance );
				placesService.getDetails( { placeId: firstPrediction.place_id }, ( place, detailsStatus ) => {
					if ( detailsStatus !== window.google.maps.places.PlacesServiceStatus.OK ) {
						return;
					}

					handleSelectedPlace( place );
				} );
			}
		);
	};

	// For Handling the selected place - used by onPlaceChanged and handleKeyDown
	const handleSelectedPlace = place => {
		const { geometry } = place;

		if ( ! geometry ) {
			return;
		}

		if ( place.geometry.viewport ) {
			mapInstance.fitBounds( place.geometry.viewport );
		} else {
			mapInstance.setCenter( place.geometry.location );
			mapInstance.setZoom( 17 );
		}

		applyPostcodeRetailers( place.geometry.location );
		setInputValue( place.formatted_address || '' );
		setCurrentLocation( place.geometry.location.toJSON() );
	};

	const onLoad = value => {
		setAutocomplete( value );
	};

	const debouncedCenter = useDebounce( center, 400 );

	const applyPostcodeRetailers = async location => {
		const postcode = await getPostcode( location );

		if ( ! postcode ) {
			setPostcodeRetailers( [] );
			return;
		}

		const postcodeRetailersRes = await getClosestRetailerPostcode( postcode );
		setPostcodeRetailers( postcodeRetailersRes );
		setLocationIsSet( true );
	};

	const getPostcode = async latLng => {
		const geocoder = new window.google.maps.Geocoder();

		const { results } = await geocoder.geocode( { location: latLng } );

		if ( ! results[ 0 ] ) {
			return;
		}

		let postcode = '';
		let country = '';

		for ( let i = 0; i < results[ 0 ].address_components.length; i++ ) {
			const addressTypes = results[ 0 ].address_components[ i ].types;
			if ( addressTypes.includes( 'postal_code' ) ) {
				postcode = results[ 0 ].address_components[ i ].long_name;
			}

			if ( addressTypes.includes( 'country' ) ) {
				country = results[ 0 ].address_components[ i ].short_name;
			}
		}

		if ( country !== 'SE' ) {
			return;
		}

		postcode = postcode.replaceAll( ' ', '' );

		return postcode;
	};

	// Return map bounds based on list of places
	const getMapBounds = ( maps, places ) => {
		const bounds = new maps.LatLngBounds();

		places.forEach( place => {
			bounds.extend( new maps.LatLng(
				place.lat,
				place.lng,
			) );
		} );

		return bounds;
	};

	const onPlaceChanged = async () => {
		if ( autocomplete !== null ) {
			const place = autocomplete.getPlace();
			handleSelectedPlace( place );
		}
	};

	const apiHasLoaded = useCallback( map => {
		const bounds = getMapBounds( window.google.maps, retailers );

		setMapBounds( bounds );
		map.fitBounds( bounds );

		setMapInstance( map );
		setMapAPI( window.google.maps );

		setTimeout( () => setShowMap( false ), 100 );
	}, [] );

	const renderClusterer = useCallback(
		clusterer => retailers.map( marker => {
			return <Pin key={ marker.id } data={ marker } setSelectedMarker={ setSelectedMarker } selectedMarker={ selectedMarker } clusterer={ clusterer } />;
		} ), [ retailers, selectedMarker ]
	);

	const panTo = useCallback( async ( { lat, lng } ) => {
		const location = new window.google.maps.LatLng( lat, lng );
		await applyPostcodeRetailers( location );

		mapInstance.panTo( { lat, lng } );
		mapInstance.setZoom( 14 );
	}, [ mapInstance ] );

	useEffect( () => {
		if ( displayMode !== 'compact' ) {
			return;
		}

		if ( currentLocation ) {
			window.localStorage.setItem( CURRENT_LOCATION_KEY, JSON.stringify( currentLocation ) );
		} else {
			const storedLocation = window.localStorage.getItem( CURRENT_LOCATION_KEY );

			// panTo requires mapInstance to be initialized.
			if ( storedLocation && mapInstance ) {
				const storedLocationObject = JSON.parse( storedLocation );
				setCurrentLocation( storedLocationObject );
				panTo( storedLocationObject );
			}
		}
	}, [ currentLocation, mapInstance ] );

	if ( loadError ) {
		return retailerTranslations.errorLoadingMap;
	}

	if ( ! isLoaded ) {
		return retailerTranslations.loadingMap;
	}

	if ( Object.keys( retailers ).length === 0 ) {
		return (
			<h2 className="retailer-map__no-retailers">{ retailerTranslations.noRetailerFound }</h2>
		);
	}

	return (
		<>
			<header className="retailer-map__header">
				{ mapBounds && (
					<>
						<div className="retailer-heading__buttons">
							<Locate panTo={ panTo } setCurrentLocation={ setCurrentLocation } />
							{ displayMode === 'compact' && <div className="wp-block-button is-style-primary-underline btn-medium">
								{ /* eslint-disable */}
								<a className="wp-block-button__link" onClick={ () => setShowMap( ! showMap ) }>
									{ showMap ? retailerTranslations.hideMap : retailerTranslations.showMap }
								</a>
								{/* eslint-enable */ }
							</div> }
						</div>
						<Autocomplete bounds={ mapBounds } onLoad={ onLoad } onPlaceChanged={ onPlaceChanged } className="retailer-map__autocomplete">
							<input
								type="text"
								placeholder={ retailerTranslations.enterALocation }
								value={ inputValue }
								onChange={ e => setInputValue( e.target.value ) }
								onKeyDown={ handleKeyDown }
							/>
						</Autocomplete>
					</>
				) }
			</header>
			<GoogleMap
				id="retailer-map"
				mapContainerStyle={ mapContainerStyle }
				defaultZoom={ 3 }
				defaultCenter={ center }
				onLoad={ map => apiHasLoaded( map ) }
				mapContainerClassName={ displayMode === 'compact' && ! showMap ? 'retailer-map__hidden' : undefined }
				onCenterChanged={ () => setCenter( mapInstance.getCenter().toJSON() ) }
				options={ {
					mapTypeControl: true,
					mapTypeControlOptions: {
						style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
						position: window.google.maps.ControlPosition.TOP_RIGHT,
					},
				} }
			>
				<MarkerClusterer options={ options }>{ renderClusterer }</MarkerClusterer>
			</GoogleMap>
			{ ( ! hideRetailersWithoutLocation || currentLocation ) && <Retailers center={ debouncedCenter } mapInstance={ mapInstance } mapAPI={ mapAPI } retailers={ retailers } locationIsSet={ locationIsSet } postcodeRetailers={ postcodeRetailers } showLogos={ showLogos } displayMode={ displayMode } perPage={ perPage } hideAddressField={ hideAddressField } hidePhoneField={ hidePhoneField } hideWebsiteField={ hideWebsiteField } /> }
		</>
	);
};

Map.defaultProps = {
	retailers: [
		{ id: 0, lat: -31.56391, lng: 147.154312 },
		{ id: 1, lat: -33.718234, lng: 150.363181 },
		{ id: 2, lat: -33.727111, lng: 150.371124 },
		{ id: 3, lat: -33.848588, lng: 151.209834 },
		{ id: 4, lat: -33.851702, lng: 151.216968 },
		{ id: 5, lat: -34.671264, lng: 150.863657 },
		{ id: 6, lat: -35.304724, lng: 148.662905 },
		{ id: 7, lat: -36.817685, lng: 175.699196 },
		{ id: 8, lat: -36.828611, lng: 175.790222 },
		{ id: 9, lat: -37.75, lng: 145.116667 },
		{ id: 10, lat: -37.759859, lng: 145.128708 },
		{ id: 11, lat: -37.765015, lng: 145.133858 },
		{ id: 12, lat: -37.770104, lng: 145.143299 },
		{ id: 13, lat: -37.7737, lng: 145.145187 },
		{ id: 14, lat: -37.774785, lng: 145.137978 },
		{ id: 15, lat: -37.819616, lng: 144.968119 },
		{ id: 16, lat: -38.330766, lng: 144.695692 },
		{ id: 17, lat: -39.927193, lng: 175.053218 },
		{ id: 18, lat: -41.330162, lng: 174.865694 },
		{ id: 19, lat: -42.734358, lng: 147.439506 },
		{ id: 20, lat: -42.734358, lng: 147.501315 },
		{ id: 21, lat: -42.735258, lng: 147.438 },
		{ id: 22, lat: -43.999792, lng: 170.463352 },
	],
	mapContainerStyle: {
		height: '380px',
		width: '100%',
	},
	options: {
		imagePath:
			'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
	},
	defaultCenter: {
		lat: 39.113766,
		lng: -94.692449,
	},
};

export default Map;
